import { useState } from 'react';

import { UNNUMBERED_TERRITORY_CODES, US_TERRITORY_CODES } from '@/utils/addressState';

interface AddressDetails {
  formatted_address: string;
  place_id: string;
  address_components: any[];
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
  // Parsed address components for voting analysis
  parsed_address?: {
    street_number?: string;
    route?: string;
    city?: string;
    county?: string;
    state?: string;
    postal_code?: string;
  };
}

export const parseAddressComponents = (addressComponents: any[]) => {
  const parsed: any = {};

  addressComponents.forEach((component) => {
    const types = component.types || [];

    if (types.includes('street_number')) {
      parsed.street_number = component.longText || component.longName || component.long_name;
    } else if (types.includes('route')) {
      parsed.route = component.longText || component.longName || component.long_name;
    } else if (types.includes('locality')) {
      parsed.city = component.longText || component.longName || component.long_name;
    } else if (types.includes('administrative_area_level_2')) {
      parsed.county = component.longText || component.longName || component.long_name;
    } else if (types.includes('administrative_area_level_1')) {
      parsed.state = component.shortText || component.shortName || component.short_name;
    } else if (types.includes('postal_code')) {
      parsed.postal_code = component.longText || component.longName || component.long_name;
    } else if (types.includes('country')) {
      parsed.country = component.shortText || component.shortName || component.short_name;
    }
  });

  if (US_TERRITORY_CODES.has(parsed.country)) parsed.state = parsed.country;

  return parsed;
};

export const useProfileForm = () => {
  const [address, setAddress] = useState('');
  const [selectedAddressDetails, setSelectedAddressDetails] = useState<AddressDetails | null>(null);
  const [hasValidAddress, setHasValidAddress] = useState(false);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [hasValidPhone, setHasValidPhone] = useState(false);
  const [phoneConsent, setPhoneConsent] = useState(false);
  const [emailConsent, setEmailConsent] = useState(false);

  // Phone number entered but invalid
  const hasInvalidPhoneError = !!phone && !hasValidPhone;
  // Phone consent given but no phone or invalid phone
  const hasPhoneConsentSelectedButInvalid = phoneConsent && (!phone || hasInvalidPhoneError);
  const hasPhoneError = hasInvalidPhoneError || hasPhoneConsentSelectedButInvalid;

  const handleAddressChange = (newAddress: string) => {
    setAddress(newAddress);

    // Reset validation if user is typing a new address
    if (!selectedAddressDetails || selectedAddressDetails.formatted_address !== newAddress) {
      setHasValidAddress(false);
      setSelectedAddressDetails(null);
    }
  };

  const validateAddressForVoting = (addressDetails: AddressDetails) => {
    const parsed = parseAddressComponents(addressDetails.address_components);

    // Check for required components for voting analysis
    const hasState = parsed.state && parsed.state.trim() !== '';
    // Villages here have no ZIP: the backend saves them by town and fills in
    // American Samoa's single ZIP.
    const hasPostalCode = UNNUMBERED_TERRITORY_CODES.has(parsed.state)
      ? !!parsed.city?.trim()
      : parsed.postal_code && parsed.postal_code.trim() !== '';

    if (!hasState || !hasPostalCode) {
      console.warn('Address missing required components for voting analysis:', {
        state: parsed.state,
        postal_code: parsed.postal_code,
      });
      return false;
    }

    return true;
  };

  const handleAddressSelect = (addressDetails: AddressDetails) => {
    const parsed = parseAddressComponents(addressDetails.address_components);
    const addressWithParsed = {
      ...addressDetails,
      parsed_address: parsed,
    };

    // Validate that we have the required components for voting analysis
    const isValidForVoting = validateAddressForVoting(addressDetails);

    if (isValidForVoting) {
      setAddressError(null);
    }

    setSelectedAddressDetails(addressWithParsed);
    setHasValidAddress(isValidForVoting);

    if (!isValidForVoting) {
      // TODO: Show user-friendly message about address requirements
      console.log('Address selected but missing required voting information');
    }
  };

  const normalizePhoneDigits = (value: string) => {
    const digitsOnly = (value || '').replace(/\D/g, '');
    const withoutCountry = digitsOnly.startsWith('1') ? digitsOnly.slice(1) : digitsOnly;
    return withoutCountry.slice(0, 10);
  };

  const validatePhone = (value: string) => {
    // Normalize once for consistent logic
    const normalized = normalizePhoneDigits(value);
    if (normalized.length !== 10) return false;

    // NANP format: NXX NXX XXXX, where N = digits 2-9 and X = digits 0-9.
    // This ensures area code and exchange code do not start with 0 or 1.
    const nanpPattern = /^[2-9]\d{2}[2-9]\d{2}\d{4}$/;
    if (!nanpPattern.test(normalized)) return false;

    // Reject obviously fake numbers like all same digits
    if (/^(\d)\1{9}$/.test(normalized)) return false;
    return true;
  };

  const formatPhone = (value: string) => {
    const limited = normalizePhoneDigits(value);

    const area = limited.slice(0, 3);
    const exchange = limited.slice(3, 6);
    const line = limited.slice(6, 10);

    if (limited.length <= 3) return area;
    if (limited.length <= 6) return `(${area}) ${exchange}`;
    return `(${area}) ${exchange}-${line}`;
  };

  const handlePhoneChange = (newVal: string) => {
    const formatted = formatPhone(newVal);
    setPhone(formatted);
    const isPhoneValid = validatePhone(formatted);
    if (phoneError && isPhoneValid) {
      setPhoneError(null);
    }
    setHasValidPhone(isPhoneValid);
  };

  const handlePhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedControlKeys = new Set([
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
      'Tab',
      'Enter',
    ]);
    const isDigit = e.key >= '0' && e.key <= '9';
    if (!isDigit && !allowedControlKeys.has(e.key)) {
      // Prevent non-digit input; formatting is applied automatically onChange
      e.preventDefault();
    }
  };

  return {
    address,
    hasValidAddress,
    addressError,
    selectedAddressDetails,
    hasValidPhone,
    emailConsent,
    phoneConsent,
    phoneError,
    phone,
    hasPhoneError,
    handleAddressChange,
    handleAddressSelect,
    handlePhoneChange,
    handlePhoneKeyDown,
    setAddress,
    setAddressError,
    setEmailConsent,
    setPhoneConsent,
    setPhoneError,
  };
};
